import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Zap, Search, MessageSquare, Users, LogOut, Menu, Loader2, Plus,
  LayoutDashboard, BarChart3, Workflow, Activity, Store,
  Shield, FileText, Copy, Check, Trash2, ChevronRight,
  CheckCircle2, AlertTriangle, Clock, Phone, Volume2, Play
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Operations = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tab, setTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [changeReqs, setChangeReqs] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ client_name: '', client_email: '', agent_ids: [] });
  const [copied, setCopied] = useState('');
  const [slaData, setSlaData] = useState({});

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [clientsRes, contractsRes, crRes, agentsRes] = await Promise.all([
        axios.get(`${API}/portal/clients`),
        axios.get(`${API}/tier/contracts`),
        axios.get(`${API}/portal/change-requests`),
        axios.get(`${API}/agents`),
      ]);
      setClients(clientsRes.data);
      setContracts(contractsRes.data);
      setChangeReqs(crRes.data);
      setAgents(agentsRes.data);
      // Fetch SLA for each agent
      const slas = {};
      for (const a of agentsRes.data.slice(0, 10)) {
        try {
          const res = await axios.get(`${API}/tier/sla/${a.id}`);
          slas[a.id] = res.data;
        } catch { /* ignore */ }
      }
      setSlaData(slas);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleInvite = async () => {
    if (!inviteForm.client_name || !inviteForm.client_email) return toast.error('Name and email required');
    try {
      const res = await axios.post(`${API}/portal/invite`, inviteForm);
      toast.success('Client invited!');
      setCopied(res.data.access_token);
      setShowInvite(false);
      setInviteForm({ client_name: '', client_email: '', agent_ids: [] });
      fetchAll();
      // Auto-copy portal URL
      const url = `${window.location.origin}/portal?token=${res.data.access_token}`;
      navigator.clipboard.writeText(url).catch(() => {});
      toast.success('Portal link copied to clipboard!');
    } catch { toast.error('Failed to invite'); }
  };

  const handleCRStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/portal/change-requests/${id}`, { status });
      toast.success(`Request ${status}`);
      fetchAll();
    } catch { toast.error('Failed to update'); }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke client access?')) return;
    try {
      await axios.delete(`${API}/portal/clients/${id}`);
      toast.success('Access revoked');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Workflow, label: 'Workflow Builder', path: '/workflow' },
    { icon: Shield, label: 'Operations', path: '/operations' },
    { icon: Activity, label: 'Agent Monitor', path: '/agents' },
    { icon: Store, label: 'Marketplace', path: '/marketplace' },
    { icon: Search, label: 'Job Leads', path: '/jobs' },
    { icon: Users, label: 'Outbound Leads', path: '/crm' },
  ];

  const getSlaColor = (score) => score >= 80 ? 'text-emerald-400' : score >= 60 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5"><Link to="/" className="flex items-center gap-2"><HSLogo size={32} /><span className="font-black text-lg tracking-tight">HumanScaled</span></Link></div>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map(item => { const Icon = item.icon; const isActive = location.pathname === item.path; return (<Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${isActive ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' : 'text-muted-foreground hover:text-white hover:bg-white/5'}`}><Icon className="w-5 h-5" />{item.label}</Link>); })}
          </nav>
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-sm mb-3"><div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">{user?.name?.charAt(0) || 'U'}</div><div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{user?.name}</p></div></div>
            <Button variant="ghost" onClick={() => { logout(); navigate('/'); }} className="w-full justify-start text-muted-foreground hover:text-white"><LogOut className="w-4 h-4 mr-2" /> Sign Out</Button>
          </div>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white"><Menu className="w-6 h-6" /></button>
              <h1 className="text-lg font-bold" data-testid="ops-title">Operations</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex bg-[#0a0a0a] border border-white/10 rounded-sm overflow-hidden">
                {[{ key: 'clients', label: 'CLIENTS' }, { key: 'sla', label: 'SLA' }, { key: 'requests', label: 'REQUESTS' }].map(t => (
                  <button key={t.key} onClick={() => setTab(t.key)} className={`px-3 py-1.5 text-xs font-mono transition-colors ${tab === t.key ? 'bg-emerald-500/20 text-emerald-400' : 'text-muted-foreground hover:text-white'}`} data-testid={`tab-${t.key}`}>{t.label}</button>
                ))}
              </div>
              <Button onClick={() => setShowInvite(true)} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-9 text-sm" data-testid="invite-btn"><Plus className="w-4 h-4 mr-1" /> Invite Client</Button>
            </div>
          </div>
        </header>

        <div className="p-6 lg:p-8 space-y-6">
          {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div> : (
            <>
              {/* CLIENTS TAB */}
              {tab === 'clients' && (
                <div className="space-y-4" data-testid="clients-tab">
                  {clients.length === 0 ? (
                    <div className="bg-[#0a0a0a] border border-white/5 p-12 text-center">
                      <Users className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-30" />
                      <p className="font-bold mb-1">No clients yet</p>
                      <p className="text-sm text-muted-foreground mb-4">Invite clients to give them portal access to their agents.</p>
                      <Button onClick={() => setShowInvite(true)} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm">Invite Client</Button>
                    </div>
                  ) : clients.map(c => (
                    <div key={c.id} className="bg-[#0a0a0a] border border-white/5 p-5 flex items-center justify-between" data-testid={`client-${c.id}`}>
                      <div>
                        <h3 className="font-bold text-sm">{c.client_name}</h3>
                        <p className="text-xs text-muted-foreground">{c.client_email} · {c.agent_ids?.length || 0} agents</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="border-white/10 rounded-sm h-7 text-xs" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/portal?token=${c.access_token}`); toast.success('Link copied'); }}>
                          <Copy className="w-3 h-3 mr-1" /> Copy Link
                        </Button>
                        <Button variant="outline" size="sm" className="border-red-500/20 text-red-400 rounded-sm h-7 text-xs" onClick={() => handleRevoke(c.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* SLA TAB */}
              {tab === 'sla' && (
                <div className="space-y-4" data-testid="sla-tab">
                  {agents.map(a => {
                    const sla = slaData[a.id];
                    return (
                      <div key={a.id} className="bg-[#0a0a0a] border border-white/5 p-5" data-testid={`sla-${a.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-bold text-sm">{a.name}</h3>
                          {sla && <span className={`font-mono text-lg font-bold ${getSlaColor(sla.sla_score)}`}>{sla.sla_score}/100</span>}
                        </div>
                        {sla ? (
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <div><p className="text-[10px] text-muted-foreground uppercase">Status</p><p className={`text-sm font-mono ${sla.status === 'passing' ? 'text-emerald-400' : sla.status === 'warning' ? 'text-amber-400' : 'text-red-400'}`}>{sla.status}</p></div>
                            <div><p className="text-[10px] text-muted-foreground uppercase">Uptime</p><p className="text-sm font-mono">{sla.component_scores?.uptime}%</p></div>
                            <div><p className="text-[10px] text-muted-foreground uppercase">Response</p><p className="text-sm font-mono">{sla.component_scores?.response_time}%</p></div>
                            <div><p className="text-[10px] text-muted-foreground uppercase">Reliability</p><p className="text-sm font-mono">{sla.component_scores?.reliability}%</p></div>
                            <div><p className="text-[10px] text-muted-foreground uppercase">Runs ({sla.period_days}d)</p><p className="text-sm font-mono">{sla.metrics?.total_runs}</p></div>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">No SLA data</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* REQUESTS TAB */}
              {tab === 'requests' && (
                <div className="space-y-3" data-testid="requests-tab">
                  {changeReqs.length === 0 ? (
                    <div className="bg-[#0a0a0a] border border-white/5 p-12 text-center">
                      <FileText className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-30" />
                      <p className="text-sm text-muted-foreground">No change requests yet</p>
                    </div>
                  ) : changeReqs.map(cr => (
                    <div key={cr.id} className="bg-[#0a0a0a] border border-white/5 p-5" data-testid={`cr-${cr.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-bold text-sm">{cr.title}</h3>
                          <p className="text-xs text-muted-foreground">{cr.client_name} · {cr.priority} priority</p>
                        </div>
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 ${
                          cr.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                          cr.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                          cr.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                          'bg-amber-500/20 text-amber-400'
                        }`}>{cr.status}</span>
                      </div>
                      <p className="text-sm text-muted-foreground bg-black/30 border border-white/5 p-2 mb-3">{cr.description}</p>
                      {cr.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button onClick={() => handleCRStatus(cr.id, 'in_progress')} size="sm" className="bg-blue-600 text-white hover:bg-blue-500 rounded-sm h-7 text-xs">Start</Button>
                          <Button onClick={() => handleCRStatus(cr.id, 'completed')} size="sm" className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm h-7 text-xs">Complete</Button>
                          <Button onClick={() => handleCRStatus(cr.id, 'rejected')} size="sm" variant="outline" className="border-red-500/20 text-red-400 rounded-sm h-7 text-xs">Reject</Button>
                        </div>
                      )}
                      {cr.status === 'in_progress' && (
                        <Button onClick={() => handleCRStatus(cr.id, 'completed')} size="sm" className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm h-7 text-xs">Mark Complete</Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Voice AI Section */}
        <div className="px-6 lg:px-8 pb-8">
          <div className="bg-violet-500/5 border border-violet-500/20 p-6 lg:p-8">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 border border-violet-500/30 bg-violet-500/10 text-violet-400 text-xs font-mono mb-4">
                  <Volume2 className="w-3.5 h-3.5" /> VOICE AI — BUILT IN
                </div>
                <h3 className="text-xl font-bold mb-3">AI That Answers Phones Like a Human</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Powered by ElevenLabs, voice AI is baked into the HumanScaled platform. Perfect for contractors, medical offices, and real estate teams. 24/7 call handling with human oversight.
                </p>
                <ul className="space-y-2 mb-4">
                  {['Books appointments on calendar', 'Qualifies leads with custom questions', 'Handles after-hours and overflow', 'Speaks multiple languages'].map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-violet-400 flex-shrink-0" /><span className="text-muted-foreground">{f}</span></li>
                  ))}
                </ul>
              </div>
              <div className="bg-black/40 border border-violet-500/30 p-5 relative">
                <div className="absolute -top-2.5 -right-2.5 px-2 py-0.5 bg-violet-500 text-white text-[9px] font-bold">ELEVENLABS POWERED</div>
                <div className="space-y-3">
                  {[
                    { speaker: 'AI', text: 'Thank you for calling Premier Roofing. This is Sarah. How may I help you today?' },
                    { speaker: 'Caller', text: "Hi, I need a roof estimate for my home." },
                    { speaker: 'AI', text: "I'd be happy to schedule that. What's your address? I'll check availability in your area." },
                  ].map((msg, i) => (
                    <div key={i} className={`flex gap-2 ${msg.speaker === 'AI' ? '' : 'justify-end'}`}>
                      <div className={`max-w-[80%] p-2.5 text-xs ${msg.speaker === 'AI' ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-white/5 border border-white/10'}`}>
                        <span className={`font-mono text-[10px] ${msg.speaker === 'AI' ? 'text-violet-400' : 'text-muted-foreground'}`}>{msg.speaker}</span>
                        <p className="mt-0.5">{msg.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground text-center mt-3">Available on Pro and Agency plans</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Invite Modal */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-md">
          <DialogHeader><DialogTitle>Invite Client</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><label className="text-xs text-muted-foreground uppercase mb-2 block">Client Name</label><Input value={inviteForm.client_name} onChange={e => setInviteForm({ ...inviteForm, client_name: e.target.value })} className="bg-black/50 border-white/10 rounded-sm" placeholder="Metro Health" data-testid="invite-name" /></div>
            <div><label className="text-xs text-muted-foreground uppercase mb-2 block">Client Email</label><Input value={inviteForm.client_email} onChange={e => setInviteForm({ ...inviteForm, client_email: e.target.value })} className="bg-black/50 border-white/10 rounded-sm" placeholder="admin@company.com" data-testid="invite-email" /></div>
            <div>
              <label className="text-xs text-muted-foreground uppercase mb-2 block">Agents to Share</label>
              <div className="space-y-1">
                {agents.map(a => (
                  <label key={a.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={inviteForm.agent_ids.includes(a.id)} onChange={e => {
                      setInviteForm(prev => ({ ...prev, agent_ids: e.target.checked ? [...prev.agent_ids, a.id] : prev.agent_ids.filter(x => x !== a.id) }));
                    }} className="accent-emerald-500" />
                    {a.name}
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={handleInvite} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm" data-testid="invite-submit">Create Portal Access</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Operations;
